import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, MessageCircle, UserPlus, UserCheck, Clock, Camera, Heart, Trash2, X, Bell } from 'lucide-react';
import { SwipaSend as Send } from '@/icons/SwipaSend';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { wallApi, type WallPost, type FullProfile } from '@/api/wall';
import { useAuthStore } from '@/store/authStore';
import Avatar from '@/components/ui/Avatar';
import clsx from 'clsx';
import NickName from '@/components/ui/NickName';

const STICKY_COLORS: Record<string, { bg: string; border: string; shadow: string }> = {
  yellow: { bg: 'bg-yellow-100', border: 'border-yellow-300', shadow: 'shadow-yellow-200/30' },
  pink:   { bg: 'bg-pink-100',   border: 'border-pink-300',   shadow: 'shadow-pink-200/30' },
  blue:   { bg: 'bg-blue-100',   border: 'border-blue-300',   shadow: 'shadow-blue-200/30' },
  green:  { bg: 'bg-green-100',  border: 'border-green-300',  shadow: 'shadow-green-200/30' },
  purple: { bg: 'bg-purple-100', border: 'border-purple-300', shadow: 'shadow-purple-200/30' },
};

function StickyNote({ post, isOwner, onLike, onDelete }: {
  post: WallPost; isOwner: boolean;
  onLike: () => void; onDelete: () => void;
}) {
  const colors = STICKY_COLORS[post.color] ?? STICKY_COLORS.yellow;
  const rotation = ((post.id.charCodeAt(0) % 7) - 3) * 0.8; // slight random tilt

  return (
    <div
      className={clsx(
        'relative p-4 rounded-sm border-2 transition-transform hover:scale-[1.02] hover:z-10',
        colors.bg, colors.border,
        'shadow-lg', colors.shadow,
      )}
      style={{ transform: `rotate(${rotation}deg)` }}
    >
      {/* Pin / magnet */}
      <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-red-500 border-2 border-red-700 shadow-md z-10" />

      {/* Author */}
      <div className="flex items-center gap-2 mb-2 pt-1">
        <Avatar src={post.author.avatarUrl} username={post.author.username} size="xs" />
        <span className="text-xs font-semibold text-gray-700"><NickName user={post.author} className="text-xs font-semibold text-gray-700" /></span>
        {post.remindAt && (
          <span className="ml-auto inline-flex items-center gap-0.5 text-[10px] text-amber-600" title={`Напоминание: ${format(new Date(post.remindAt), 'd MMM HH:mm', { locale: ru })}`}>
            <Bell size={10} />
          </span>
        )}
      </div>

      {/* Content */}
      <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed font-handwriting">
        {post.content}
      </p>

      {/* Actions — like (left), trash (owner), date (bottom-right) */}
      <div className="flex items-center gap-3 mt-3 pt-2 border-t border-black/5">
        <button
          onClick={onLike}
          className={clsx('flex items-center gap-1 text-xs transition-colors',
            post.likedByMe ? 'text-red-500' : 'text-gray-400 hover:text-red-400')}
        >
          <Heart size={14} fill={post.likedByMe ? 'currentColor' : 'none'} />
          {post._count.likes > 0 && post._count.likes}
        </button>
        {isOwner && (
          <button onClick={onDelete} className="text-xs text-gray-300 hover:text-red-400">
            <Trash2 size={12} />
          </button>
        )}
        <span className="ml-auto text-[10px] text-gray-400">
          {format(new Date(post.createdAt), 'd MMM HH:mm', { locale: ru })}
        </span>
      </div>
    </div>
  );
}

export default function ProfilePage() {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const { user: me } = useAuthStore();
  const qc = useQueryClient();
  const identifier = `@${username}`;
  const isOwnProfile = username === me?.username;

  const { data: profile, isLoading, error: profileError } = useQuery({
    queryKey: ['profile-full', identifier],
    queryFn: () => wallApi.getFullProfile(identifier),
    enabled: !!username,
  });

  const { data: postsPage } = useQuery({
    queryKey: ['wall-posts', identifier],
    queryFn: () => wallApi.listPosts(identifier),
    enabled: !!username,
  });

  const [newPost, setNewPost] = useState('');
  const [selectedColor, setSelectedColor] = useState('yellow');
  const [remindAt, setRemindAt] = useState<string>(''); // datetime-local value
  const [expiresDays, setExpiresDays] = useState<number>(7);
  const [createError, setCreateError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () => {
      const expiresIso = new Date(Date.now() + expiresDays * 24 * 60 * 60 * 1000).toISOString();
      const remindIso = remindAt ? new Date(remindAt).toISOString() : null;
      return wallApi.createPost(identifier, newPost, selectedColor, undefined, {
        expiresAt: expiresIso,
        remindAt: remindIso,
      });
    },
    onSuccess: () => {
      setNewPost('');
      setRemindAt('');
      setCreateError(null);
      qc.invalidateQueries({ queryKey: ['wall-posts', identifier] });
    },
    onError: (e: any) => setCreateError(e?.response?.data?.message ?? 'Ошибка'),
  });

  const likeMut = useMutation({
    mutationFn: (args: { postId: string; liked: boolean }) =>
      args.liked ? wallApi.unlikePost(args.postId) : wallApi.likePost(args.postId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wall-posts', userId] }),
  });

  const deleteMut = useMutation({
    mutationFn: (postId: string) => wallApi.deletePost(postId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wall-posts', userId] }),
  });

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-surface-950">
        <div className="animate-spin w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (profileError || !profile) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-surface-950 gap-3">
        <p className="text-surface-400 text-sm">Не удалось загрузить профиль @{username}</p>
        <p className="text-surface-600 text-xs">{(profileError as Error)?.message ?? 'Профиль не найден'}</p>
        <button onClick={() => navigate(-1)} className="btn-ghost text-xs text-brand-400">Назад</button>
      </div>
    );
  }

  const { user: u, stats, recentPhotos, stories, friendship } = profile;
  const posts = postsPage?.data ?? [];
  const friendStatus = friendship?.status?.toUpperCase();
  const canPost = isOwnProfile || (u.wallPrivacy === 'ALL') || (u.wallPrivacy === 'FRIENDS' && friendStatus === 'ACCEPTED');

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-surface-950 overflow-y-auto">
      {/* Header */}
      <div className="h-12 border-b border-surface-800 px-4 flex items-center gap-3 flex-shrink-0">
        <button onClick={() => navigate(-1)} className="btn-ghost p-1.5 text-surface-400">
          <ArrowLeft size={18} />
        </button>
        <span className="font-semibold text-surface-200">Профиль</span>
      </div>

      {/* Profile Header */}
      <div className="bg-gradient-to-b from-brand-900/30 to-surface-950 px-6 py-8">
        <div className="max-w-2xl mx-auto flex items-start gap-5">
          <div className="relative">
            <Avatar src={u.avatarUrl} username={u.username} size="lg" />
            <div className={clsx('absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-surface-950',
              u.status === 'ONLINE' ? 'bg-green-500' : 'bg-surface-600'
            )} />
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-surface-100"><NickName user={u} className="text-xl font-bold text-surface-100" /></h1>
            <p className="text-surface-400 text-sm">@{u.username}</p>
            {u.bio && <p className="text-surface-300 text-sm mt-2">{u.bio}</p>}
            <div className="flex items-center gap-4 mt-3 text-xs text-surface-500">
              <span>{stats.friends} друзей</span>
              <span>{stats.photos} фото</span>
              <span>{stats.posts} записей</span>
              <span className="flex items-center gap-1">
                <Clock size={12} />
                {format(new Date(u.createdAt), 'd MMMM yyyy', { locale: ru })}
              </span>
            </div>
            {/* Actions */}
            {!isOwnProfile && (
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => navigate(`/dm/${u.id}`)}
                  className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5"
                >
                  <MessageCircle size={14} /> Написать
                </button>
                {(!friendship || friendship.status === 'none') && (
                  <button className="btn-ghost text-xs py-1.5 px-3 border border-surface-700 flex items-center gap-1.5">
                    <UserPlus size={14} /> Добавить в друзья
                  </button>
                )}
                {friendStatus === 'PENDING' && (
                  <span className="text-xs text-surface-500 py-1.5 px-3 flex items-center gap-1.5">
                    <Clock size={14} /> Заявка отправлена
                  </span>
                )}
                {friendStatus === 'ACCEPTED' && (
                  <span className="text-xs text-green-400 py-1.5 px-3 flex items-center gap-1.5">
                    <UserCheck size={14} /> В друзьях
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Photos preview */}
      {recentPhotos.length > 0 && (
        <div className="max-w-2xl mx-auto w-full px-6 mt-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-surface-200 font-semibold text-sm flex items-center gap-2">
              <Camera size={16} className="text-brand-400" /> Фото ({stats.photos})
            </h2>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {recentPhotos.map((p) => (
              <div key={p.id} className="aspect-square rounded-lg overflow-hidden bg-surface-800">
                {p.media.thumbnailPath && (
                  <img
                    src={`/uploads/thumbnails/${p.media.thumbnailPath.split('/').pop()}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stories */}
      {stories.length > 0 && (
        <div className="max-w-2xl mx-auto w-full px-6 mt-6">
          <h2 className="text-surface-200 font-semibold text-sm mb-3">Stories</h2>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {stories.map((s) => (
              <div key={s.id} className="w-16 h-16 rounded-full border-2 border-brand-500 overflow-hidden flex-shrink-0">
                {s.mediaFile.thumbnailPath && (
                  <img
                    src={`/uploads/thumbnails/${s.mediaFile.thumbnailPath.split('/').pop()}`}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Fridge / Wall */}
      <div className="max-w-2xl mx-auto w-full px-6 mt-8 pb-8">
        <h2 className="text-surface-200 font-semibold text-sm mb-4">
          Тикеты на холодильник
        </h2>

        {/* New ticket input */}
        {canPost && (
          <div className="bg-surface-900 rounded-xl border border-surface-800 p-4 mb-6">
            <textarea
              value={newPost}
              onChange={(e) => setNewPost(e.target.value)}
              placeholder="Оставить записку на холодильник..."
              rows={3}
              maxLength={2000}
              className="w-full bg-transparent text-surface-200 text-sm resize-none outline-none placeholder:text-surface-500"
            />
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <div className="flex gap-1">
                {Object.keys(STICKY_COLORS).map((c) => (
                  <button
                    key={c}
                    onClick={() => setSelectedColor(c)}
                    className={clsx(
                      'w-6 h-6 rounded-full border-2 transition-transform',
                      STICKY_COLORS[c].bg,
                      selectedColor === c ? 'scale-110 border-white' : 'border-transparent opacity-60',
                    )}
                  />
                ))}
              </div>

              <label className="flex items-center gap-1.5 text-[11px] text-surface-400">
                Срок:
                <select
                  value={expiresDays}
                  onChange={(e) => setExpiresDays(Number(e.target.value))}
                  className="bg-surface-800 border border-surface-700 rounded px-1.5 py-0.5 text-[11px] text-surface-200"
                >
                  <option value={1}>1 день</option>
                  <option value={3}>3 дня</option>
                  <option value={7}>1 неделя</option>
                  <option value={14}>2 недели</option>
                  <option value={30}>Месяц</option>
                </select>
              </label>

              <label className="flex items-center gap-1.5 text-[11px] text-surface-400" title="Владельцу придёт push в этот момент (максимум 1 напоминание/день на друга)">
                <Bell size={11} className="text-amber-400" /> Напомнить:
                <input
                  type="datetime-local"
                  value={remindAt}
                  onChange={(e) => setRemindAt(e.target.value)}
                  className="bg-surface-800 border border-surface-700 rounded px-1.5 py-0.5 text-[11px] text-surface-200"
                />
                {remindAt && (
                  <button type="button" onClick={() => setRemindAt('')} className="text-surface-500 hover:text-surface-300">
                    <X size={10} />
                  </button>
                )}
              </label>

              <button
                onClick={() => createMut.mutate()}
                disabled={!newPost.trim() || createMut.isPending}
                className="btn-primary text-xs py-1.5 px-4 flex items-center gap-1.5 disabled:opacity-40 ml-auto"
              >
                <Send size={12} /> Приклеить
              </button>
            </div>
            {createError && (
              <p className="text-red-400 text-xs mt-2">{createError}</p>
            )}
          </div>
        )}

        {/* CSS Fridge */}
        <div className="relative max-w-[520px] mx-auto">
          {/* Fridge body */}
          <div
            className="relative rounded-[12px] overflow-hidden"
            style={{
              background: 'linear-gradient(160deg, #d4d8dc 0%, #b8bfc6 15%, #c8cdd3 30%, #a8b0b8 50%, #bcc3ca 70%, #9ea7b0 100%)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.4), inset 0 -2px 0 rgba(0,0,0,0.15)',
              minHeight: '550px',
            }}
          >
            {/* Brushed metal texture */}
            <div
              className="absolute inset-0 pointer-events-none opacity-[0.07]"
              style={{ background: 'repeating-linear-gradient(90deg, transparent, transparent 1px, rgba(0,0,0,0.15) 1px, transparent 2px)' }}
            />

            {/* Top edge / cap */}
            <div
              className="h-[14px] rounded-t-[12px]"
              style={{
                background: 'linear-gradient(180deg, #e0e4e8 0%, #c0c6cc 50%, #a8b0b8 100%)',
                borderBottom: '1px solid rgba(0,0,0,0.1)',
              }}
            />

            {/* Handle */}
            <div
              className="absolute left-[5%] top-[15%] bottom-[20%] w-[6%] z-10 rounded-[4px]"
              style={{
                background: 'linear-gradient(90deg, #8a9199 0%, #c8cdd3 30%, #e4e8ec 50%, #c8cdd3 70%, #8a9199 100%)',
                boxShadow: '-2px 0 6px rgba(0,0,0,0.2), 2px 0 4px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.5)',
              }}
            />

            {/* Content area — tickets go here */}
            <div className="relative pl-[16%] pr-[6%] py-8 overflow-hidden" style={{ minHeight: '500px' }}>
              {posts.length === 0 && (
                <div className="flex flex-col items-center justify-center min-h-[420px]" style={{ color: '#7a8490' }}>
                  <span className="text-4xl mb-3">🧲</span>
                  <p className="text-sm font-semibold">Холодильник пустой</p>
                  <p className="text-xs mt-1 opacity-70">Будь первым, кто приклеит записку!</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 relative">
                {posts.map((post) => (
                  <StickyNote
                    key={post.id}
                    post={post}
                    isOwner={post.authorId === me?.id || post.ownerId === me?.id}
                    onLike={() => likeMut.mutate({ postId: post.id, liked: post.likedByMe })}
                    onDelete={() => { if (confirm('Удалить записку?')) deleteMut.mutate(post.id); }}
                  />
                ))}
              </div>
            </div>

            {/* Bottom edge */}
            <div
              className="h-[10px]"
              style={{
                background: 'linear-gradient(180deg, #a8b0b8 0%, #8a9199 50%, #6e7680 100%)',
                borderTop: '1px solid rgba(0,0,0,0.08)',
              }}
            />
          </div>

          {/* Feet */}
          <div className="flex justify-between px-[8%]">
            <div className="w-[40px] h-[14px] rounded-b-[4px]"
              style={{ background: 'linear-gradient(180deg, #6e7680, #555d65)', boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }} />
            <div className="w-[40px] h-[14px] rounded-b-[4px]"
              style={{ background: 'linear-gradient(180deg, #6e7680, #555d65)', boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }} />
          </div>
        </div>
      </div>
    </div>
  );
}
